// frontend/src/components/maps/RoutesMap.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  LuArchive,
  LuArrowLeft,
  LuChevronLeft,
  LuChevronRight,
  LuClock3,
  LuCopy,
  LuFlag,
  LuGripVertical,
  LuLoaderCircle,
  LuMapPin,
  LuPencil,
  LuPlus,
  LuRoute,
  LuSave,
  LuSearch,
  LuTrash2,
} from "react-icons/lu";

import { useTranslation } from "react-i18next";
import Sortable from "sortablejs";

import api from "../../axiosConfig";
import RoutePlannerMap from "./RoutePlannerMap";
import RoutePointSearch from "./RoutePointSearch";

import "../../styles/RoutesMap.css";

const createEmptyDraft = () => ({
  id: null,
  name: "",
  description: "",
  avoidTolls: false,
  avoidFerries: false,
  avoidHighways: false,
  points: [],
});

function normalizeDraftPoints(points) {
  const origin = points.find(
    (point) => point.type === "ORIGIN"
  );

  const destination = points.find(
    (point) => point.type === "DESTINATION"
  );

  const intermediatePoints = points
    .filter(
      (point) =>
        point.type !== "ORIGIN" &&
        point.type !== "DESTINATION"
    )
    .sort((a, b) => {
      return (
        (Number(a.sequence) || 0) -
        (Number(b.sequence) || 0)
      );
    });

  return [
    origin,
    ...intermediatePoints,
    destination,
  ]
    .filter(Boolean)
    .map((point, index) => ({
      ...point,
      sequence: index + 1,
    }));
}

function normalizeApiPoint(point, index) {
  return {
    ...point,
    id: point.id,
    type: String(
      point.type || "VIA"
    ).toUpperCase(),
    sequence:
      Number(point.sequence) ||
      index + 1,
    lat: Number(
      point.latitude ?? point.lat
    ),
    lng: Number(
      point.longitude ??
      point.lng ??
      point.lon
    ),
  };
}

function normalizeApiRoute(route) {
  if (!route) return null;

  return {
    ...route,
    id: Number(route.id),
    routeNumber:
      Number(route.routeNumber) || null,
    version:
      Number(route.version) || 1,
    distanceKm:
      route.distanceKm === null
        ? null
        : Number(route.distanceKm),
    durationSeconds:
      route.durationSeconds === null
        ? null
        : Number(route.durationSeconds),
    tollAmount:
      route.tollAmount === null
        ? null
        : Number(route.tollAmount),
    tollMinimum:
      route.tollMinimum === null
        ? null
        : Number(route.tollMinimum),
    tollMaximum:
      route.tollMaximum === null
        ? null
        : Number(route.tollMaximum),
    source:
      route.routeSource ||
      route.source ||
      "SAVED_ROUTE",
    points: normalizeDraftPoints(
      (Array.isArray(route.points)
        ? route.points
        : []
      ).map(normalizeApiPoint)
    ),
  };
}

function getResponseItem(data) {
  return normalizeApiRoute(
    data?.item ??
    data?.route ??
    data
  );
}

function buildDraftFromRoute(route) {
  const normalized =
    normalizeApiRoute(route);

  if (!normalized) {
    return createEmptyDraft();
  }

  return {
    id: normalized.id,
    name: normalized.name || "",
    description:
      normalized.description || "",
    avoidTolls:
      Boolean(normalized.avoidTolls),
    avoidFerries:
      Boolean(normalized.avoidFerries),
    avoidHighways:
      Boolean(normalized.avoidHighways),
    points: normalized.points,
  };
}

function buildSavePayload(
  routeDraft,
  routeResult
) {
  return {
    name: routeDraft.name.trim(),
    description:
      routeDraft.description.trim() ||
      null,
    vehicleProfile: "truck",
    avoidTolls:
      Boolean(routeDraft.avoidTolls),
    avoidFerries:
      Boolean(routeDraft.avoidFerries),
    avoidHighways:
      Boolean(routeDraft.avoidHighways),

    points: normalizeDraftPoints(
      routeDraft.points
    ).map((point, index) => ({
      type: point.type,
      sequence: index + 1,
      name: point.name || null,
      address: point.address || null,
      postalCode:
        point.postalCode || null,
      city: point.city || null,
      country: point.country || null,
      lat: point.lat,
      lng: point.lng,
      mandatory:
        point.mandatory !== false,
      plannedStopMinutes:
        point.plannedStopMinutes ??
        null,
      instructions:
        point.instructions || null,
    })),

    route: routeResult
      ? {
          distanceKm:
            routeResult.distanceKm,
          durationSeconds:
            routeResult.durationSeconds,
          hasTolls:
            Boolean(routeResult.hasTolls),
          tollEstimate:
            routeResult.tollEstimate ||
            null,
          geometry:
            routeResult.geometry ||
            null,
          source:
            routeResult.source ||
            routeResult.routeSource ||
            null,
        }
      : null,
  };
}

function formatCoordinates(point) {
  if (!point) return null;

  const lat = Number(point.lat);
  const lng = Number(point.lng);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }

  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function getPointPrimaryText(
  point,
  fallback = ""
) {
  return (
    point?.name ||
    point?.city ||
    fallback ||
    formatCoordinates(point)
  );
}

function getPointSecondaryText(point) {
  if (point?.address) {
    return point.address;
  }

  const parts = [
    point?.postalCode,
    point?.city,
    point?.country,
  ].filter(Boolean);

  if (parts.length) {
    return parts.join(", ");
  }

  return formatCoordinates(point);
}

function formatKm(value, locale) {
  const number = Number(value);

  if (
    !Number.isFinite(number) ||
    number <= 0
  ) {
    return null;
  }

  const formatted =
    new Intl.NumberFormat(locale, {
      minimumFractionDigits:
        number >= 100 ? 0 : 1,
      maximumFractionDigits:
        number >= 100 ? 0 : 1,
    }).format(number);

  return `${formatted} km`;
}

function formatDuration(seconds) {
  const number = Number(seconds);

  if (
    !Number.isFinite(number) ||
    number <= 0
  ) {
    return null;
  }

  const totalMinutes =
    Math.round(number / 60);

  const hours =
    Math.floor(totalMinutes / 60);

  const minutes =
    totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes} min`;
  }

  if (minutes <= 0) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes} min`;
}

function formatCurrency(
  value,
  currency,
  locale
) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency || "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatRouteTollEstimate(
  route,
  locale
) {
  const currency =
    route?.tollCurrency ||
    route?.currency ||
    "EUR";

  const amount =
    Number(route?.tollAmount);

  if (
    Number.isFinite(amount) &&
    amount > 0
  ) {
    return formatCurrency(
      amount,
      currency,
      locale
    );
  }

  const minimum =
    Number(route?.tollMinimum);

  const maximum =
    Number(route?.tollMaximum);

  const hasMinimum =
    Number.isFinite(minimum) &&
    minimum > 0;

  const hasMaximum =
    Number.isFinite(maximum) &&
    maximum > 0;

  if (
    hasMinimum &&
    hasMaximum &&
    Math.abs(
      maximum - minimum
    ) < 0.01
  ) {
    return formatCurrency(
      maximum,
      currency,
      locale
    );
  }

  if (
    hasMinimum &&
    hasMaximum
  ) {
    return [
      formatCurrency(
        minimum,
        currency,
        locale
      ),
      formatCurrency(
        maximum,
        currency,
        locale
      ),
    ].join(" – ");
  }

  if (hasMaximum) {
    return formatCurrency(
      maximum,
      currency,
      locale
    );
  }

  if (hasMinimum) {
    return formatCurrency(
      minimum,
      currency,
      locale
    );
  }

  return null;
}

const RoutesMap = () => {
  const { t, i18n } =
    useTranslation("map");

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt-PT";

  const [mode, setMode] =
    useState("list");

  const [searchTerm, setSearchTerm] =
    useState("");

  const [
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  ] = useState(false);

  const [routes, setRoutes] =
    useState([]);

  const [routesLoading, setRoutesLoading] =
    useState(true);

  const [routesError, setRoutesError] =
    useState(null);

  const [
    selectedRoute,
    setSelectedRoute,
  ] = useState(null);

  const [
    selectedRouteLoading,
    setSelectedRouteLoading,
  ] = useState(false);

  const [routeDraft, setRouteDraft] =
    useState(createEmptyDraft);

  const viaListRef =
    useRef(null);

  const latestRouteRequestRef =
    useRef(0);

  const [pickingType, setPickingType] =
    useState(null);

  const [routeState, setRouteState] =
    useState({
      route: null,
      loading: false,
      error: null,
    });

  const [isSaving, setIsSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState(null);

  const fetchRouteDetails =
    useCallback(
      async (routeId) => {
        const requestId =
          latestRouteRequestRef.current + 1;

        latestRouteRequestRef.current =
          requestId;

        if (!routeId) {
          setSelectedRoute(null);
          setSelectedRouteLoading(false);
          return null;
        }

        setSelectedRouteLoading(true);

        try {
          const { data } =
            await api.get(
              `/route-templates/${routeId}`
            );

          const item =
            getResponseItem(data);

          if (
            latestRouteRequestRef.current !==
            requestId
          ) {
            return null;
          }

          setSelectedRoute(item);

          return item;
        } catch (error) {
          if (
            latestRouteRequestRef.current !==
            requestId
          ) {
            return null;
          }

          console.error(
            "[RoutesMap] Failed to load route:",
            error
          );

          setRoutesError(
            t(
              "map.routes.list.errors.loadDetail",
              {
                defaultValue:
                  "Não foi possível carregar a rota.",
              }
            )
          );

          return null;
        } finally {
          if (
            latestRouteRequestRef.current ===
            requestId
          ) {
            setSelectedRouteLoading(false);
          }
        }
      },
      [t]
    );

  const refreshRoutes =
    useCallback(
      async (preferredRouteId = null) => {
        setRoutesLoading(true);
        setRoutesError(null);

        try {
          const { data } =
            await api.get(
              "/route-templates"
            );

          const items = (
            Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data)
                ? data
                : []
          ).map(normalizeApiRoute);

          setRoutes(items);

          const currentSelectedId =
            preferredRouteId ||
            null;

          const target =
            items.find(
              (item) =>
                Number(item.id) ===
                Number(currentSelectedId)
            ) ||
            items[0] ||
            null;

          if (target) {
            await fetchRouteDetails(
              target.id
            );
          } else {
            setSelectedRoute(null);
          }
        } catch (error) {
          console.error(
            "[RoutesMap] Failed to load routes:",
            error
          );

          setRoutesError(
            t(
              "map.routes.list.errors.load",
              {
                defaultValue:
                  "Não foi possível carregar as rotas.",
              }
            )
          );
        } finally {
          setRoutesLoading(false);
        }
      },
      [
        fetchRouteDetails,
        t,
      ]
    );

  useEffect(() => {
    refreshRoutes();
  }, [refreshRoutes]);

  const filteredRoutes =
    useMemo(() => {
      const cleanSearch =
        searchTerm.trim().toLowerCase();

      if (!cleanSearch) {
        return routes;
      }

      return routes.filter((route) => {
        return [
          route.name,
          route.description,
          route.routeNumber,
        ]
          .map((value) =>
            String(value || "")
              .toLowerCase()
          )
          .some((value) =>
            value.includes(cleanSearch)
          );
      });
    }, [routes, searchTerm]);

  const origin = useMemo(() => {
    return routeDraft.points.find(
      (point) =>
        point.type === "ORIGIN"
    );
  }, [routeDraft.points]);

  const destination = useMemo(() => {
    return routeDraft.points.find(
      (point) =>
        point.type === "DESTINATION"
    );
  }, [routeDraft.points]);

  const viaPoints = useMemo(() => {
    return routeDraft.points.filter(
      (point) =>
        point.type !== "ORIGIN" &&
        point.type !== "DESTINATION"
    );
  }, [routeDraft.points]);

  const selectedDistance = formatKm(
    routeState.route?.distanceKm,
    locale
  );

  const selectedDuration =
    formatDuration(
      routeState.route?.durationSeconds
    );

  const tollEstimate =
    routeState.route?.tollEstimate;

  const selectedTollAmount =
    formatCurrency(
      tollEstimate?.amount,
      tollEstimate?.currency ||
        "EUR",
      locale
    );

  const isBuilder =
    mode === "create" ||
    mode === "edit";

  useEffect(() => {
    let secondFrame = null;

    const notifyResize = () => {
      window.dispatchEvent(
        new Event("resize")
      );
    };

    const firstFrame =
      window.requestAnimationFrame(
        () => {
          notifyResize();

          secondFrame =
            window.requestAnimationFrame(
              notifyResize
            );
        }
      );

    const timer =
      window.setTimeout(
        notifyResize,
        60
      );

    return () => {
      window.cancelAnimationFrame(
        firstFrame
      );

      if (secondFrame) {
        window.cancelAnimationFrame(
          secondFrame
        );
      }

      window.clearTimeout(
        timer
      );
    };
  }, [
    isBuilder,
    isSidebarCollapsed,
  ]);

  const canSave =
    Boolean(routeDraft.name.trim()) &&
    Boolean(origin) &&
    Boolean(destination) &&
    Boolean(
      routeState.route?.geometry
    ) &&
    !routeState.loading &&
    !isSaving;

  const handleCreateRoute = () => {
    setRouteDraft(createEmptyDraft());
    setPickingType(null);
    setSaveError(null);
    setRouteState({
      route: null,
      loading: false,
      error: null,
    });
    setMode("create");
  };

  const handleEditRoute = () => {
    if (!selectedRoute) return;

    setRouteDraft(
      buildDraftFromRoute(selectedRoute)
    );

    setRouteState({
      route: selectedRoute,
      loading: false,
      error: null,
    });

    setPickingType(null);
    setSaveError(null);
    setMode("edit");
  };

  const handleCancelBuilder = () => {
    setRouteDraft(createEmptyDraft());
    setPickingType(null);
    setSaveError(null);
    setRouteState({
      route: null,
      loading: false,
      error: null,
    });
    setMode("list");
  };

  const handlePointPicked = ({
    type,
    lat,
    lng,
    name = null,
    address = null,
    postalCode = null,
    city = null,
    country = null,
    countryCode = null,
  }) => {
    const normalizedType =
      String(type || "VIA").toUpperCase();

    setRouteDraft((current) => {
      const nextPoint = {
        id:
          normalizedType === "VIA"
            ? `via-${Date.now()}-${Math.random()
                .toString(16)
                .slice(2)}`
            : normalizedType.toLowerCase(),

        type: normalizedType,

        lat: Number(lat),
        lng: Number(lng),

        name,
        address,
        postalCode,
        city,
        country,
        countryCode,

        sequence:
          current.points.length + 1,
      };

      let nextPoints;

      if (
        normalizedType === "ORIGIN" ||
        normalizedType === "DESTINATION"
      ) {
        nextPoints =
          current.points.filter(
            (point) =>
              point.type !==
              normalizedType
          );

        nextPoints.push(nextPoint);
      } else {
        nextPoints = [
          ...current.points,
          nextPoint,
        ];
      }

      return {
        ...current,
        points:
          normalizeDraftPoints(
            nextPoints
          ),
      };
    });

    setPickingType(null);
    setSaveError(null);
  };

  const handleSearchPointSelected = (
    type,
    place
  ) => {
    handlePointPicked({
      ...place,
      type,
    });
  };

  const handlePointMoved =
    useCallback(
      (updatedPoint) => {
        if (!updatedPoint?.id) {
          return;
        }

        setRouteDraft((current) => ({
          ...current,

          points:
            normalizeDraftPoints(
              current.points.map(
                (point) => {
                  if (
                    String(point.id) !==
                    String(
                      updatedPoint.id
                    )
                  ) {
                    return point;
                  }

                  return {
                    ...point,
                    ...updatedPoint,

                    id: point.id,
                    type: point.type,
                    sequence:
                      point.sequence,
                  };
                }
              )
            ),
        }));

        setPickingType(null);
        setSaveError(null);
      },
      []
    );

  const handleRemovePoint = (
    pointId
  ) => {
    setRouteDraft((current) => ({
      ...current,
      points: normalizeDraftPoints(
        current.points.filter(
          (point) =>
            String(point.id) !==
            String(pointId)
        )
      ),
    }));

    setPickingType(null);
    setSaveError(null);
  };

  const handleReorderViaPoints =
    useCallback(
      (
        oldIndex,
        newIndex
      ) => {
        if (
          oldIndex === newIndex ||
          oldIndex < 0 ||
          newIndex < 0
        ) {
          return;
        }

        setRouteDraft((current) => {
          const currentOrigin =
            current.points.find(
              (point) =>
                point.type ===
                "ORIGIN"
            );

          const currentDestination =
            current.points.find(
              (point) =>
                point.type ===
                "DESTINATION"
            );

          const currentViaPoints =
            current.points
              .filter(
                (point) =>
                  point.type !==
                    "ORIGIN" &&
                  point.type !==
                    "DESTINATION"
              )
              .sort((a, b) => {
                return (
                  (Number(
                    a.sequence
                  ) || 0) -
                  (Number(
                    b.sequence
                  ) || 0)
                );
              });

          const [movedPoint] =
            currentViaPoints.splice(
              oldIndex,
              1
            );

          if (!movedPoint) {
            return current;
          }

          currentViaPoints.splice(
            newIndex,
            0,
            movedPoint
          );

          return {
            ...current,

            points:
              normalizeDraftPoints(
                [
                  currentOrigin,
                  ...currentViaPoints,
                  currentDestination,
                ].filter(Boolean)
              ),
          };
        });

        setSaveError(null);
      },
      []
    );

  useEffect(() => {
    if (
      !isBuilder ||
      !viaListRef.current ||
      viaPoints.length < 2
    ) {
      return undefined;
    }

    const sortable =
      Sortable.create(
        viaListRef.current,
        {
          animation: 180,

          handle:
            ".route-builder__via-drag",

          draggable:
            ".route-builder__via-row",

          ghostClass:
            "is-sortable-ghost",

          chosenClass:
            "is-sortable-chosen",

          dragClass:
            "is-sortable-dragging",

          onEnd: (event) => {
            handleReorderViaPoints(
              Number(event.oldIndex),
              Number(event.newIndex)
            );
          },
        }
      );

    return () => {
      sortable.destroy();
    };
  }, [
    handleReorderViaPoints,
    isBuilder,
    viaPoints.length,
  ]);

  const handleSaveRoute = async () => {
    if (!routeDraft.name.trim()) {
      setSaveError(
        t(
          "map.routes.builder.validation.name",
          {
            defaultValue:
              "Introduza um nome para a rota.",
          }
        )
      );
      return;
    }

    if (!origin || !destination) {
      setSaveError(
        t(
          "map.routes.builder.validation.points",
          {
            defaultValue:
              "Selecione uma origem e um destino.",
          }
        )
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const payload =
        buildSavePayload(
          routeDraft,
          routeState.route
        );

      const response =
        mode === "edit" &&
        routeDraft.id
          ? await api.put(
              `/route-templates/${routeDraft.id}`,
              payload
            )
          : await api.post(
              "/route-templates",
              payload
            );

      const savedRoute =
        getResponseItem(
          response.data
        );

      setMode("list");
      setRouteDraft(createEmptyDraft());
      setPickingType(null);
      setRouteState({
        route: null,
        loading: false,
        error: null,
      });

      await refreshRoutes(
        savedRoute?.id || null
      );
    } catch (error) {
      console.error(
        "[RoutesMap] Failed to save route:",
        error
      );

      setSaveError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t(
          "map.routes.builder.errors.save",
          {
            defaultValue:
              "Não foi possível guardar a rota.",
          }
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicateRoute =
    async (route) => {
      try {
        const copySuffix = t(
          "map.routes.actions.copySuffix",
          {
            defaultValue: "Cópia",
          }
        );

        const { data } =
          await api.post(
            `/route-templates/${route.id}/duplicate`,
            {
              name:
                `${route.name} - ${copySuffix}`,
            }
          );

        const duplicated =
          getResponseItem(data);

        await refreshRoutes(
          duplicated?.id || null
        );
      } catch (error) {
        console.error(
          "[RoutesMap] Failed to duplicate route:",
          error
        );

        setRoutesError(
          t(
            "map.routes.list.errors.duplicate",
            {
              defaultValue:
                "Não foi possível duplicar a rota.",
            }
          )
        );
      }
    };

  const handleArchiveRoute =
    async (route) => {
      const accepted =
        window.confirm(
          t(
            "map.routes.actions.archiveConfirm",
            {
              name: route.name,
              defaultValue:
                `Arquivar a rota "${route.name}"?`,
            }
          )
        );

      if (!accepted) return;

      try {
        await api.delete(
          `/route-templates/${route.id}`
        );

        const remainingRoutes =
          routes.filter(
            (item) =>
              Number(item.id) !==
              Number(route.id)
          );

        setRoutes(remainingRoutes);

        const nextRoute =
          remainingRoutes[0] ||
          null;

        if (nextRoute) {
          await fetchRouteDetails(
            nextRoute.id
          );
        } else {
          setSelectedRoute(null);
        }
      } catch (error) {
        console.error(
          "[RoutesMap] Failed to archive route:",
          error
        );

        setRoutesError(
          t(
            "map.routes.list.errors.archive",
            {
              defaultValue:
                "Não foi possível arquivar a rota.",
            }
          )
        );
      }
    };

  return (
    <div
      className={[
        "map-view",
        isBuilder
          ? "is-route-builder"
          : "",
        isSidebarCollapsed
          ? "is-sidebar-collapsed"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <aside
        className={[
          "map-view__sidebar",
          isBuilder
            ? "map-view__sidebar--builder"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {!isBuilder ? (
          <>
            <div className="routes-panel__header">
              <div>
                <h2 className="map-view__sidebar-title">
                  {t(
                    "map.routes.title",
                    {
                      defaultValue:
                        "Rotas",
                    }
                  )}
                </h2>

                <p className="map-view__sidebar-description">
                  {t(
                    "map.routes.description",
                    {
                      defaultValue:
                        "Crie e reutilize rotas planeadas para futuras sessões de transporte.",
                    }
                  )}
                </p>
              </div>

              <button
                type="button"
                className="routes-panel__create-button"
                onClick={handleCreateRoute}
              >
                <LuPlus />

                <span>
                  {t(
                    "map.routes.actions.create",
                    {
                      defaultValue:
                        "Criar rota",
                    }
                  )}
                </span>
              </button>
            </div>

            <label className="routes-panel__search">
              <LuSearch aria-hidden="true" />

              <input
                type="search"
                value={searchTerm}
                onChange={(event) =>
                  setSearchTerm(
                    event.target.value
                  )
                }
                placeholder={t(
                  "map.routes.list.searchPlaceholder",
                  {
                    defaultValue:
                      "Pesquisar rotas...",
                  }
                )}
              />
            </label>

            {routesError && (
              <div className="routes-panel__error">
                <span>{routesError}</span>

                <button
                  type="button"
                  onClick={() =>
                    refreshRoutes()
                  }
                >
                  {t(
                    "map.routes.list.retry",
                    {
                      defaultValue:
                        "Tentar novamente",
                    }
                  )}
                </button>
              </div>
            )}

            {routesLoading ? (
              <div className="routes-panel__loading">
                <LuLoaderCircle />

                <span>
                  {t(
                    "map.routes.list.loading",
                    {
                      defaultValue:
                        "A carregar rotas...",
                    }
                  )}
                </span>
              </div>
            ) : filteredRoutes.length > 0 ? (
              <div className="routes-panel__list">
                {filteredRoutes.map(
                  (route) => {
                    const distance =
                      formatKm(
                        route.distanceKm,
                        locale
                      );

                    const duration =
                      formatDuration(
                        route.durationSeconds
                      );

                    const routePoints =
                      Array.isArray(
                        route.points
                      )
                        ? route.points
                        : [];

                    const routeOrigin =
                      routePoints.find(
                        (point) =>
                          point.type ===
                          "ORIGIN"
                      ) ||
                      routePoints[0] ||
                      null;

                    const routeDestination =
                      routePoints.find(
                        (point) =>
                          point.type ===
                          "DESTINATION"
                      ) ||
                      routePoints[
                        routePoints.length -
                          1
                      ] ||
                      null;

                    const routeOriginName =
                      getPointPrimaryText(
                        routeOrigin,
                        t(
                          "map.routes.list.originFallback",
                          {
                            defaultValue:
                              "Origem não definida",
                          }
                        )
                      );

                    const routeDestinationName =
                      getPointPrimaryText(
                        routeDestination,
                        t(
                          "map.routes.list.destinationFallback",
                          {
                            defaultValue:
                              "Destino não definido",
                          }
                        )
                      );

                    const routeOriginSecondaryText =
                      getPointSecondaryText(
                        routeOrigin
                      );

                    const routeDestinationSecondaryText =
                      getPointSecondaryText(
                        routeDestination
                      );

                    const routeOriginSecondary =
                      routeOriginSecondaryText &&
                      routeOriginSecondaryText !==
                        routeOriginName
                        ? routeOriginSecondaryText
                        : null;

                    const routeDestinationSecondary =
                      routeDestinationSecondaryText &&
                      routeDestinationSecondaryText !==
                        routeDestinationName
                        ? routeDestinationSecondaryText
                        : null;

                    const tollEstimate =
                      formatRouteTollEstimate(
                        route,
                        locale
                      );

                    const isSelected =
                      Number(
                        selectedRoute?.id
                      ) ===
                      Number(route.id);

                    return (
                      <article
                        key={route.id}
                        className={[
                          "routes-panel__card",
                          isSelected
                            ? "is-selected"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          fetchRouteDetails(
                            route.id
                          )
                        }
                        onKeyDown={(event) => {
                          if (
                            event.key ===
                              "Enter" ||
                            event.key === " "
                          ) {
                            event.preventDefault();

                            fetchRouteDetails(
                              route.id
                            );
                          }
                        }}
                      >
                        <div className="routes-panel__card-top">
                          <div>
                            <span className="routes-panel__number">
                              {`R-${String(
                                route.routeNumber ||
                                  route.id
                              ).padStart(
                                4,
                                "0"
                              )}`}
                            </span>

                            <strong>
                              {route.name}
                            </strong>
                          </div>

                          <span className="routes-panel__version">
                            v{route.version || 1}
                          </span>
                        </div>

                        <div className="routes-panel__card-points">
                          <span
                            className="routes-panel__card-connection"
                            aria-hidden="true"
                          />

                          <div className="routes-panel__card-point">
                            <span className="routes-panel__card-point-icon is-origin">
                              <LuMapPin
                                aria-hidden="true"
                              />
                            </span>

                            <div className="routes-panel__card-point-copy">
                              <span>
                                {t(
                                  "map.routes.list.origin",
                                  {
                                    defaultValue:
                                      "Origem",
                                  }
                                )}
                              </span>

                              <strong>
                                {routeOriginName}
                              </strong>

                              {routeOriginSecondary && (
                                <small>
                                  {
                                    routeOriginSecondary
                                  }
                                </small>
                              )}
                            </div>
                          </div>

                          <div className="routes-panel__card-point">
                            <span className="routes-panel__card-point-icon is-destination">
                              <LuFlag
                                aria-hidden="true"
                              />
                            </span>

                            <div className="routes-panel__card-point-copy">
                              <span>
                                {t(
                                  "map.routes.list.destination",
                                  {
                                    defaultValue:
                                      "Destino",
                                  }
                                )}
                              </span>

                              <strong>
                                {
                                  routeDestinationName
                                }
                              </strong>

                              {routeDestinationSecondary && (
                                <small>
                                  {
                                    routeDestinationSecondary
                                  }
                                </small>
                              )}
                            </div>
                          </div>
                        </div>

                        {(duration ||
                          distance ||
                          tollEstimate) && (
                          <div className="routes-panel__card-meta">
                            {duration && (
                              <span>
                                <LuClock3
                                  aria-hidden="true"
                                />

                                {duration}
                              </span>
                            )}

                            {distance && (
                              <span>
                                <LuRoute
                                  aria-hidden="true"
                                />

                                {distance}
                              </span>
                            )}

                            {tollEstimate && (
                              <span>
                                {t(
                                  "map.routes.list.estimatedTolls",
                                  {
                                    amount:
                                      tollEstimate,

                                    defaultValue:
                                      "Portagens: {{amount}}",
                                  }
                                )}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="routes-panel__card-footer">
                          <span>
                            {t(
                              "map.routes.list.pointCount",
                              {
                                count:
                                  route.points
                                    ?.length ||
                                  0,
                                defaultValue:
                                  "{{count}} pontos",
                              }
                            )}
                          </span>

                          <div className="routes-panel__card-actions">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();

                                fetchRouteDetails(
                                  route.id
                                ).then(
                                  (loaded) => {
                                    if (
                                      loaded
                                    ) {
                                      setRouteDraft(
                                        buildDraftFromRoute(
                                          loaded
                                        )
                                      );

                                      setRouteState({
                                        route:
                                          loaded,
                                        loading:
                                          false,
                                        error:
                                          null,
                                      });

                                      setMode(
                                        "edit"
                                      );
                                    }
                                  }
                                );
                              }}
                              aria-label={t(
                                "map.routes.actions.edit",
                                {
                                  defaultValue:
                                    "Editar rota",
                                }
                              )}
                            >
                              <LuPencil />
                            </button>

                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();

                                handleDuplicateRoute(
                                  route
                                );
                              }}
                              aria-label={t(
                                "map.routes.actions.duplicate",
                                {
                                  defaultValue:
                                    "Duplicar rota",
                                }
                              )}
                            >
                              <LuCopy />
                            </button>

                            <button
                              type="button"
                              className="is-danger"
                              onClick={(event) => {
                                event.stopPropagation();

                                handleArchiveRoute(
                                  route
                                );
                              }}
                              aria-label={t(
                                "map.routes.actions.archive",
                                {
                                  defaultValue:
                                    "Arquivar rota",
                                }
                              )}
                            >
                              <LuArchive />
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  }
                )}
              </div>
            ) : routes.length > 0 ? (
              <div className="map-view__sidebar-empty">
                <LuSearch />

                <strong>
                  {t(
                    "map.routes.list.noResultsTitle",
                    {
                      defaultValue:
                        "Nenhuma rota encontrada",
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "map.routes.list.noResultsDescription",
                    {
                      defaultValue:
                        "Experimente outro termo de pesquisa.",
                    }
                  )}
                </span>
              </div>
            ) : (
              <div className="map-view__sidebar-empty">
                <div
                  className="map-view__sidebar-empty-icon"
                  aria-hidden="true"
                >
                  <LuRoute />
                </div>

                <strong>
                  {t(
                    "map.routes.list.emptyTitle",
                    {
                      defaultValue:
                        "Ainda não existem rotas",
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "map.routes.list.emptyDescription",
                    {
                      defaultValue:
                        "Crie a primeira rota reutilizável para começar.",
                    }
                  )}
                </span>

                <button
                  type="button"
                  className="routes-panel__empty-action"
                  onClick={handleCreateRoute}
                >
                  <LuPlus />

                  <span>
                    {t(
                      "map.routes.actions.createFirst",
                      {
                        defaultValue:
                          "Criar primeira rota",
                      }
                    )}
                  </span>
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="route-builder__header">
              <button
                type="button"
                className="route-builder__back"
                onClick={
                  handleCancelBuilder
                }
                aria-label={t(
                  "map.routes.builder.backAria",
                  {
                    defaultValue:
                      "Voltar à lista de rotas",
                  }
                )}
              >
                <LuArrowLeft />
              </button>

              <div>
                <div className="route-builder__title-row">
                  <h2 className="map-view__sidebar-title">
                    {mode === "edit"
                      ? t(
                          "map.routes.builder.editTitle",
                          {
                            defaultValue:
                              "Editar rota",
                          }
                        )
                      : t(
                          "map.routes.builder.title",
                          {
                            defaultValue:
                              "Nova rota",
                          }
                        )}
                  </h2>

                  <span className="route-builder__draft-badge">
                    {t(
                      "map.routes.builder.draft",
                      {
                        defaultValue:
                          "Rascunho",
                      }
                    )}
                  </span>
                </div>

                <p className="map-view__sidebar-description">
                  {t(
                    "map.routes.builder.description",
                    {
                      defaultValue:
                        "Defina os dados e selecione os pontos diretamente no mapa.",
                    }
                  )}
                </p>
              </div>
            </div>

            <div className="route-builder__form">
              <label className="route-builder__field">
                <span>
                  {t(
                    "map.routes.builder.fields.name",
                    {
                      defaultValue:
                        "Nome da rota",
                    }
                  )}
                </span>

                <input
                  type="text"
                  value={routeDraft.name}
                  onChange={(event) => {
                    setRouteDraft(
                      (current) => ({
                        ...current,
                        name:
                          event.target.value,
                      })
                    );

                    setSaveError(null);
                  }}
                  placeholder={t(
                    "map.routes.builder.fields.namePlaceholder",
                    {
                      defaultValue:
                        "Ex.: Porto → Lyon",
                    }
                  )}
                />
              </label>

              <label className="route-builder__field">
                <span>
                  {t(
                    "map.routes.builder.fields.description",
                    {
                      defaultValue:
                        "Descrição",
                    }
                  )}
                </span>

                <textarea
                  value={
                    routeDraft.description
                  }
                  onChange={(event) => {
                    setRouteDraft(
                      (current) => ({
                        ...current,
                        description:
                          event.target.value,
                      })
                    );
                  }}
                  placeholder={t(
                    "map.routes.builder.fields.descriptionPlaceholder",
                    {
                      defaultValue:
                        "Notas internas sobre esta rota...",
                    }
                  )}
                  rows={3}
                />
              </label>

              <section className="route-builder__section">
                <div className="route-builder__section-header">
                  <div>
                    <strong>
                      {t(
                        "map.routes.builder.points.title",
                        {
                          defaultValue:
                            "Pontos da rota",
                        }
                      )}
                    </strong>

                    <small>
                      {t(
                        "map.routes.builder.points.hint",
                        {
                          defaultValue:
                            "Selecione um campo e clique no mapa.",
                        }
                      )}
                    </small>
                  </div>
                </div>

                <div className="route-builder__points">
                  <RoutePointSearch
                    pointType="ORIGIN"
                    title={t(
                      "map.routes.builder.points.origin",
                      {
                        defaultValue:
                          "Origem",
                      }
                    )}
                    point={origin}
                    icon={<LuMapPin />}
                    isMapPicking={
                      pickingType ===
                      "ORIGIN"
                    }
                    onSelect={(place) =>
                      handleSearchPointSelected(
                        "ORIGIN",
                        place
                      )
                    }
                    onToggleMapPicking={() => {
                      setPickingType(
                        (current) =>
                          current ===
                          "ORIGIN"
                            ? null
                            : "ORIGIN"
                      );
                    }}
                    onRemove={
                      origin
                        ? () =>
                            handleRemovePoint(
                              origin.id
                            )
                        : undefined
                    }
                  />

<div
                    ref={viaListRef}
                    className="route-builder__via-list"
                  >
                    {viaPoints.map(
                      (point, index) => (
                        <div
                          key={point.id}
                          className="route-builder__via-row"
                        >
                          <button
                            type="button"
                            className="route-builder__via-drag"
                            aria-label={t(
                              "map.routes.builder.points.dragViaAria",
                              {
                                number:
                                  index + 1,
                                defaultValue:
                                  `Reordenar ponto de passagem ${index + 1}`,
                              }
                            )}
                          >
                            <LuGripVertical />
                          </button>

                          <div className="route-builder__via-index">
                            {index + 1}
                          </div>

                          <div className="route-builder__via-copy">
                            <strong>
                              {getPointPrimaryText(
                                point,
                                t(
                                  "map.routes.builder.points.via",
                                  {
                                    number:
                                      index +
                                      1,
                                    defaultValue:
                                      `Ponto de passagem ${index + 1}`,
                                  }
                                )
                              )}
                            </strong>

                            <small>
                              {getPointSecondaryText(
                                point
                              )}
                            </small>
                          </div>

                          <button
                            type="button"
                            className="route-builder__via-remove"
                            onClick={() =>
                              handleRemovePoint(
                                point.id
                              )
                            }
                            aria-label={t(
                              "map.routes.builder.points.removeViaAria",
                              {
                                number:
                                  index + 1,
                                defaultValue:
                                  `Remover ponto de passagem ${index + 1}`,
                              }
                            )}
                          >
                            <LuTrash2 />
                          </button>
                        </div>
                      )
                    )}
                  </div>

                  <RoutePointSearch
                    pointType="VIA"
                    title={t(
                      "map.routes.builder.points.addVia",
                      {
                        defaultValue:
                          "Adicionar ponto de passagem",
                      }
                    )}
                    point={null}
                    icon={<LuPlus />}
                    isMapPicking={
                      pickingType === "VIA"
                    }
                    onSelect={(place) =>
                      handleSearchPointSelected(
                        "VIA",
                        place
                      )
                    }
                    onToggleMapPicking={() => {
                      setPickingType(
                        (current) =>
                          current === "VIA"
                            ? null
                            : "VIA"
                      );
                    }}
                  />

                  <RoutePointSearch
                    pointType="DESTINATION"
                    title={t(
                      "map.routes.builder.points.destination",
                      {
                        defaultValue:
                          "Destino",
                      }
                    )}
                    point={destination}
                    icon={<LuFlag />}
                    isMapPicking={
                      pickingType ===
                      "DESTINATION"
                    }
                    onSelect={(place) =>
                      handleSearchPointSelected(
                        "DESTINATION",
                        place
                      )
                    }
                    onToggleMapPicking={() => {
                      setPickingType(
                        (current) =>
                          current ===
                          "DESTINATION"
                            ? null
                            : "DESTINATION"
                      );
                    }}
                    onRemove={
                      destination
                        ? () =>
                            handleRemovePoint(
                              destination.id
                            )
                        : undefined
                    }
                  />
                </div>
              </section>

              <section className="route-builder__section">
                <div className="route-builder__section-header">
                  <div>
                    <strong>
                      {t(
                        "map.routes.builder.options.title",
                        {
                          defaultValue:
                            "Preferências",
                        }
                      )}
                    </strong>
                  </div>
                </div>

                <label className="route-builder__switch-row">
                  <span className="route-builder__switch-copy">
                    <strong>
                      {t(
                        "map.routes.builder.options.avoidTolls",
                        {
                          defaultValue:
                            "Evitar portagens",
                        }
                      )}
                    </strong>

                    <small>
                      {t(
                        "map.routes.builder.options.avoidTollsDescription",
                        {
                          defaultValue:
                            "Procura uma alternativa sem portagens sempre que possível.",
                        }
                      )}
                    </small>
                  </span>

                  <input
                    type="checkbox"
                    checked={
                      routeDraft.avoidTolls
                    }
                    onChange={(event) => {
                      setRouteDraft(
                        (current) => ({
                          ...current,
                          avoidTolls:
                            event.target.checked,
                        })
                      );
                    }}
                  />

                  <span
                    className="route-builder__switch"
                    aria-hidden="true"
                  />
                </label>
              </section>

              {(origin || destination) && (
                <section className="route-builder__summary">
                  <div className="route-builder__summary-header">
                    <LuRoute />

                    <strong>
                      {t(
                        "map.routes.builder.summary.title",
                        {
                          defaultValue:
                            "Resumo da rota",
                        }
                      )}
                    </strong>
                  </div>

                  {!origin || !destination ? (
                    <p>
                      {t(
                        "map.routes.builder.summary.missingPoints",
                        {
                          defaultValue:
                            "Selecione a origem e o destino para calcular o trajeto.",
                        }
                      )}
                    </p>
                  ) : routeState.loading ? (
                    <p>
                      {t(
                        "map.routes.builder.summary.calculating",
                        {
                          defaultValue:
                            "A calcular o trajeto...",
                        }
                      )}
                    </p>
                  ) : (
                    <>
                      <div className="route-builder__summary-values">
                        {selectedDuration && (
                          <div>
                            <span>
                              {t(
                                "map.routes.builder.summary.duration",
                                {
                                  defaultValue:
                                    "Duração",
                                }
                              )}
                            </span>

                            <strong>
                              {selectedDuration}
                            </strong>
                          </div>
                        )}

                        {selectedDistance && (
                          <div>
                            <span>
                              {t(
                                "map.routes.builder.summary.distance",
                                {
                                  defaultValue:
                                    "Distância",
                                }
                              )}
                            </span>

                            <strong>
                              {selectedDistance}
                            </strong>
                          </div>
                        )}
                      </div>

                      {selectedTollAmount && (
                        <div className="route-builder__toll-summary">
                          <span>
                            {t(
                              "map.routes.builder.summary.estimatedTolls",
                              {
                                defaultValue:
                                  "Portagens estimadas",
                              }
                            )}
                          </span>

                          <strong>
                            {selectedTollAmount}
                          </strong>
                        </div>
                      )}

                      {routeState.error && (
                        <div className="route-builder__route-warning">
                          {routeState.error}
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {saveError && (
                <div className="route-builder__save-error">
                  {saveError}
                </div>
              )}
            </div>

            <div className="route-builder__footer">
              <button
                type="button"
                className="route-builder__cancel-button"
                onClick={
                  handleCancelBuilder
                }
                disabled={isSaving}
              >
                {t(
                  "map.routes.actions.cancel",
                  {
                    defaultValue:
                      "Cancelar",
                  }
                )}
              </button>

              <button
                type="button"
                className="route-builder__save-button"
                onClick={
                  handleSaveRoute
                }
                disabled={!canSave}
              >
                {isSaving ? (
                  <LuLoaderCircle className="is-spinning" />
                ) : (
                  <LuSave />
                )}

                <span>
                  {isSaving
                    ? t(
                        "map.routes.actions.saving",
                        {
                          defaultValue:
                            "A guardar...",
                        }
                      )
                    : mode === "edit"
                      ? t(
                          "map.routes.actions.update",
                          {
                            defaultValue:
                              "Guardar alterações",
                          }
                        )
                      : t(
                          "map.routes.actions.save",
                          {
                            defaultValue:
                              "Guardar rota",
                          }
                        )}
                </span>
              </button>
            </div>
          </>
        )}
      </aside>

      <button
        type="button"
        className="map-view__sidebar-toggle"
        onClick={() =>
          setIsSidebarCollapsed(
            (current) => !current
          )
        }
        aria-label={
          isSidebarCollapsed
            ? t(
                "map.sidebar.show",
                {
                  defaultValue:
                    "Mostrar painel",
                }
              )
            : t(
                "map.sidebar.hide",
                {
                  defaultValue:
                    "Ocultar painel",
                }
              )
        }
        title={
          isSidebarCollapsed
            ? t(
                "map.sidebar.show",
                {
                  defaultValue:
                    "Mostrar painel",
                }
              )
            : t(
                "map.sidebar.hide",
                {
                  defaultValue:
                    "Ocultar painel",
                }
              )
        }
      >
        {isSidebarCollapsed ? (
          <LuChevronRight
            aria-hidden="true"
          />
        ) : (
          <LuChevronLeft
            aria-hidden="true"
          />
        )}
      </button>

      <section className="map-view__canvas">
        <RoutePlannerMap
          ariaLabel={t(
            "map.planner.ariaLabel",
            {
              defaultValue:
                "Planeador de rotas",
            }
          )}
          points={
            isBuilder
              ? routeDraft.points
              : selectedRoute?.points ||
                []
          }
          pickingType={
            isBuilder
              ? pickingType
              : null
          }
          onPointPicked={
            handlePointPicked
          }
          onPointMoved={
            isBuilder
              ? handlePointMoved
              : undefined
          }
          avoidTolls={
            isBuilder
              ? routeDraft.avoidTolls
              : Boolean(
                  selectedRoute
                    ?.avoidTolls
                )
          }
          onRouteChange={
            isBuilder
              ? setRouteState
              : undefined
          }
          routeOverride={
            !isBuilder
              ? selectedRoute
              : null
          }
          calculateRoute={isBuilder}
        />

        {selectedRouteLoading && (
          <div className="routes-map__selection-loading">
            <LuLoaderCircle />

            <span>
              {t(
                "map.routes.list.loadingRoute",
                {
                  defaultValue:
                    "A carregar rota...",
                }
              )}
            </span>
          </div>
        )}

        {!isBuilder &&
          selectedRoute &&
          !selectedRouteLoading && (
            <div className="routes-map__selected-actions">
              <strong>
                {selectedRoute.name}
              </strong>

              <button
                type="button"
                onClick={handleEditRoute}
              >
                <LuPencil />

                <span>
                  {t(
                    "map.routes.actions.edit",
                    {
                      defaultValue:
                        "Editar rota",
                    }
                  )}
                </span>
              </button>
            </div>
          )}
      </section>
    </div>
  );
};

export default RoutesMap;